import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../App";
import {
  MdArrowBack,
  MdSearch,
  MdLocationOn,
  MdOutlineShoppingBag,
  MdOutlineCategory,
  MdOutlineStar,
} from "react-icons/md";
import ProductCard from "../pages/ProductCard";

// API Endpoints
const PRODUCTS_API = "https://server.havbit.in/api/public/products";
const CATEGORIES_API = "https://server.havbit.in/api/categories";

const CityProductsPage = () => {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, updateQty, removeFromCart, cart, wishlist, toggleWishlistItem } = useCart();
  
  // Safely get city name
  const cityName = location.state?.cityName || 
    (citySlug ? citySlug.charAt(0).toUpperCase() + citySlug.slice(1) : "City");
  
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [priceRange, setPriceRange] = useState([0, 10000]);

  // Get cart quantities
  const cartQuantities = Array.isArray(cart) ? cart.reduce((acc, item) => {
    if (item && item._id) {
      acc[item._id] = item.qty || 1;
    }
    return acc;
  }, {}) : {};

  const isProductInWishlist = (productId) => {
    return Array.isArray(wishlist) && wishlist.some(item => item && item._id === productId);
  };

  useEffect(() => {
    fetchCityProducts();
  }, [citySlug]);

  const fetchCityProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch products and categories in parallel
      const [productsRes, categoriesRes] = await Promise.all([
        fetch(PRODUCTS_API),
        fetch(CATEGORIES_API)
      ]);
      
      if (!productsRes.ok) throw new Error(`Failed to fetch products. Status: ${productsRes.status}`);
      if (!categoriesRes.ok) throw new Error(`Failed to fetch categories. Status: ${categoriesRes.status}`);
      
      const productsData = await productsRes.json();
      const categoriesData = await categoriesRes.json();
      
      // Set all categories
      setAllCategories(categoriesData);
      
      let allProducts = [];
      if (productsData.success && productsData.data) {
        allProducts = Array.isArray(productsData.data) ? productsData.data : [];
      } else if (productsData.products) {
        allProducts = Array.isArray(productsData.products) ? productsData.products : [];
      } else if (Array.isArray(productsData)) {
        allProducts = productsData;
      }
      
      console.log("All Products:", allProducts.length);
      console.log("City Slug:", citySlug);
      
      // Filter products by State field (not religion)
      const cityProducts = allProducts.filter(product => {
        if (!product || !product.State) return false;
        
        const stateValue = String(product.State).toLowerCase().trim();
        const targetCity = String(citySlug || "").toLowerCase().trim();
        
        // Check for exact match or contains
        return stateValue === targetCity || stateValue.includes(targetCity);
      });
      
      console.log("City Products Found:", cityProducts.length);
      
      // Enhance products with proper category names
      const enhancedProducts = cityProducts.map((product, index) => {
        // Get category name from categories API
        let categoryName = "Uncategorized";
        let categoryId = product.category;
        
        if (categoryId) {
          const matchedCategory = categoriesData.find(cat => cat._id === categoryId);
          if (matchedCategory) {
            categoryName = matchedCategory.name;
          }
        }
        
        // Get subcategory name if available
        let subcategoryName = "";
        if (product.subcategory && typeof product.subcategory === 'string') {
          subcategoryName = product.subcategory;
        }
        
        const price = Number(product.price || 0);
        const originalPrice = Number(product.oldPrice || price * 1.2 || 0);
        let discount = 0;
        
        if (originalPrice > 0 && price > 0 && originalPrice > price) {
          discount = Math.round(((originalPrice - price) / originalPrice) * 100);
        }
        
        // Get image from product
        let imageUrl = product.image;
        if (!imageUrl && product.gallery && product.gallery.length > 0) {
          imageUrl = product.gallery[0];
        }
        if (!imageUrl) {
          imageUrl = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400";
        }
        
        return {
          ...product,
          id: product.id || product._id || `product-${index}`,
          name: String(product.name || "Unnamed Product"),
          price: price,
          originalPrice: originalPrice,
          discount: discount,
          rating: parseFloat((Math.random() * 1 + 4).toFixed(1)),
          reviewCount: Math.floor(Math.random() * 500) + 50,
          stock: Number(product.stock || 0),
          weight: String(product.customWeight || product.size || "100g"),
          image: imageUrl,
          description: String(product.description || "No description available"),
          category: categoryName,
          categoryId: categoryId,
          subcategory: subcategoryName,
          city: product.State || ""
        };
      });
      
      setProducts(enhancedProducts);
      setFilteredProducts(enhancedProducts);
      
      // Get unique categories from products
      const categorySet = new Set(["all"]);
      enhancedProducts.forEach(p => {
        if (p.category && p.category !== "Uncategorized") {
          categorySet.add(p.category);
        }
      });
      
      setCategories(Array.from(categorySet));
      
      console.log("Categories:", Array.from(categorySet));
      console.log("Enhanced Products:", enhancedProducts);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching city products:", error);
      setError(error.message || "Failed to load products");
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = [...products];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(product => 
        product.name.toLowerCase().includes(term) || 
        (product.description && product.description.toLowerCase().includes(term)) ||
        (product.brandName && product.brandName.toLowerCase().includes(term))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== "all") {
      result = result.filter(product => product.category === selectedCategory);
    }
    
    // Apply price filter
    result = result.filter(product => {
      const price = product.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price;
        case "price-high":
          return b.price - a.price;
        case "rating":
          return b.rating - a.rating;
        case "discount":
          return b.discount - a.discount;
        case "newest":
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default: // featured
          return 0;
      }
    });
    
    setFilteredProducts(result);
  }, [searchTerm, selectedCategory, sortBy, priceRange, products]);

  const handleBack = () => navigate(-1);

  const handleAddToCart = (product, quantity = 1) => {
    if (!product || !product.id || !addToCart) return;
    const cartItem = {
      _id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
      qty: quantity
    };
    addToCart(cartItem, quantity);
  };

  const handleQuantityUpdate = (productId, change) => {
    const currentQty = cartQuantities[productId] || 0;
    const newQty = currentQty + change;
    if (newQty < 1) {
        removeFromCart && removeFromCart(productId);
    } else {
        updateQty && updateQty(productId, newQty);
    }
  };

  const handleToggleWishlist = (product) => {
    if (!toggleWishlistItem) return;
    toggleWishlistItem({ 
      _id: product.id, 
      name: product.name,
      price: product.price,
      image: product.image,
      originalPrice: product.originalPrice,
      category: product.category,
      discount: product.discount
    });
  };

  // Popular cities
  const popularCities = [
    { name: "Delhi", slug: "delhi" },
    { name: "Mumbai", slug: "mumbai" },
    { name: "Lucknow", slug: "lucknow" },
    { name: "Bangalore", slug: "bangalore" },
    { name: "Hyderabad", slug: "hyderabad" },
    { name: "Chennai", slug: "chennai" },
    { name: "Kolkata", slug: "kolkata" },
    { name: "Pune", slug: "pune" },
    { name: "Ahmedabad", slug: "ahmedabad" },
    { name: "Jaipur", slug: "jaipur" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-xs">Loading products from {cityName}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <MdOutlineShoppingBag className="w-6 h-6 text-red-500" />
          </div>
          <h3 className="text-sm font-bold text-gray-900 mb-1.5">Oops! Something went wrong</h3>
          <p className="text-xs text-gray-600 mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={fetchCityProducts}
              className="px-4 py-2 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 shadow transition-all"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={handleBack} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50">
                <MdArrowBack className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-sm sm:text-base font-semibold text-gray-900">{cityName}</h1>
                <p className="text-xs text-gray-500">{products.length} products available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-3 sm:px-4 my-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-700 mb-1.5">Categories</p>
            <div className="flex overflow-x-auto gap-1.5 pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat 
                      ? "bg-red-500 text-white shadow shadow-red-200" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {cat === "all" ? "All" : cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1.5">Sort by</p>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full sm:w-40 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Highest Rated</option>
              <option value="discount">Best Discount</option>
              <option value="newest">Newest First</option>
            </select>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="mt-3">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={`Search products in ${cityName}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg placeholder-gray-500 text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-3 sm:px-4 pb-8">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {selectedCategory === "all" ? "All Products" : selectedCategory}
              </h2>
              <p className="text-xs text-gray-500">Found {filteredProducts.length} items</p>
            </div>
            {filteredProducts.length > 0 && (
              <div className="text-xs text-gray-600">
                Showing 1-{Math.min(filteredProducts.length, 20)} of {filteredProducts.length}
              </div>
            )}
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <MdOutlineShoppingBag className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-bold text-gray-900 mb-1.5">
              {searchTerm ? "No products found" : "No products in this city yet"}
            </h3>
            <p className="text-xs text-gray-600 mb-5 max-w-sm mx-auto">
              {searchTerm 
                ? `We couldn't find any products matching "${searchTerm}" in ${cityName}` 
                : `Check back soon for new arrivals in ${cityName}!`}
            </p>
            <div className="flex gap-2 justify-center">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all"
                >
                  Clear Search
                </button>
              )}
              <button
                onClick={() => setSelectedCategory("all")}
                className="px-4 py-2 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 shadow transition-all"
              >
                View All Products
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                onToggleWishlist={handleToggleWishlist}
                onQuantityUpdate={handleQuantityUpdate}
                isWishlisted={isProductInWishlist(product.id)}
                cartQuantity={cartQuantities[product.id] || 0}
                navigate={navigate}
              />
            ))}
          </div>
        )}

        {/* Explore Other Cities */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <MdLocationOn className="text-gray-400" />
            <p className="text-sm font-medium text-gray-900">Explore Other Cities</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {popularCities.map((city) => (
              city.slug !== citySlug?.toLowerCase() && (
                <button
                  key={city.slug}
                  onClick={() => navigate(`/city/${city.slug}`, { state: { cityName: city.name } })}
                  className="px-4 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-100 hover:text-red-500 transition-all flex items-center gap-2"
                >
                  <MdLocationOn className="text-gray-400" />
                  {city.name}
                </button>
              )
            ))}
          </div>
        </div>

        {/* Stats */}
        
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default CityProductsPage;